import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import supabase from '../../supabase/supabase';

/**
 * AuthCallback - Handles Supabase auth redirects (password reset, email confirmation, etc.)
 * 
 * This component is needed because:
 * 1. Supabase sends tokens as hash fragments (#access_token=...)
 * 2. Our app uses HashRouter which also uses # for routing
 * 3. This creates a conflict that breaks the normal flow
 * 
 * Solution: This page is served at /auth/callback (without hash router)
 * and handles the token exchange before redirecting to the app.
 */
const AuthCallback = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState('Processing...');
  const [error, setError] = useState(null);

  useEffect(() => {
    const handleAuthCallback = async () => {
      try {
        // Parse tokens from URL - handle both formats:
        // 1. Direct: /auth/callback#access_token=...
        // 2. HashRouter: /#/auth/callback#access_token=... (tokens after second #)
        let hashString = window.location.hash;
        
        // If using HashRouter, the hash might be: #/auth/callback#access_token=...
        // We need to extract the part after the second #
        const secondHashIndex = hashString.indexOf('#', 1);
        if (secondHashIndex !== -1) {
          hashString = hashString.substring(secondHashIndex);
        }
        
        // Also check the full URL for tokens (Supabase sometimes appends to path)
        const fullUrl = window.location.href;
        const tokenMatch = fullUrl.match(/[#&?](access_token=[^&]+.*)/);
        if (tokenMatch) {
          hashString = '#' + tokenMatch[1];
        }
        
        const hashParams = new URLSearchParams(hashString.substring(1));
        const accessToken = hashParams.get('access_token');
        const refreshToken = hashParams.get('refresh_token');
        const type = hashParams.get('type');

        console.log('Auth callback type:', type);

        if (type === 'recovery' && accessToken && refreshToken) {
          // Password reset flow
          setStatus('Verifying reset link...');
          
          // Set the session using the tokens from the URL
          const { data, error: sessionError } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });

          if (sessionError) {
            console.error('Session error:', sessionError);
            setError('Invalid or expired reset link. Please request a new one.');
            return;
          }

          if (data.session) {
            setStatus('Redirecting to password reset...');
            // Redirect to the reset password page (within HashRouter)
            navigate('/reset-password');
          } else {
            setError('Failed to verify reset link. Please try again.');
          }
        } else if (type === 'signup' || type === 'email_change') {
          // Email confirmation flow
          setStatus('Confirming email...');
          
          if (accessToken && refreshToken) {
            const { error: sessionError } = await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: refreshToken,
            });

            if (sessionError) {
              console.error('Session error:', sessionError);
              setError('Failed to confirm email. Please try again.');
              return;
            }
          }

          setStatus('Email confirmed! Redirecting...');
          navigate('/email-confirmed');
        } else {
          // Unknown type or missing tokens - check if already authenticated
          const { data: { session } } = await supabase.auth.getSession();
          
          if (session) {
            navigate('/dashboard');
          } else {
            setError('Invalid authentication link.');
          }
        }
      } catch (err) {
        console.error('Auth callback error:', err);
        setError('An error occurred. Please try again.');
      }
    };

    handleAuthCallback();
  }, [navigate]);

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
          <div className="bg-gradient-to-r from-red-600 to-orange-600 px-8 py-6 text-white">
            <h1 className="text-2xl font-bold text-center">Error</h1>
          </div>
          <div className="p-8 text-center">
            <p className="text-gray-600 mb-6">{error}</p>
            <a
              href="/#/forgot-password"
              className="inline-block px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors"
            >
              Request New Reset Link
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 flex items-center justify-center p-4">
      <div className="text-center">
        <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-gray-600 font-medium">{status}</p>
      </div>
    </div>
  );
};

export default AuthCallback;
